import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LoginForm } from './LoginForm';
import { SignupForm } from './SignupForm';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { LandingNav } from '../Landing/LandingNav';

export function AuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'signup' ? false : true;
  const [isLogin, setIsLogin] = useState(initialMode);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const { user, isRecoverySession } = useAuth();
  const navigate = useNavigate();
  
  // User is considered logged in only if they have a user AND it's not a recovery session
  const isLoggedIn = user !== null && !isRecoverySession;

  // Redirect to dashboard when logged in
  // Note: Pending subscription handling is done by AuthConfirmPage (email confirmation flow)
  // This simple redirect ensures Tab A doesn't interfere with Tab B's subscription handling
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  // Update mode when URL parameter changes
  useEffect(() => {
    const mode = searchParams.get('mode');
    setIsLogin(mode !== 'signup');
  }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  const heroImage = '/images/styles-page/modern_staging_style.webp';

  return (
    <div className="bg-alabaster text-charcoal min-h-screen">
      {/* Floating Navigation */}
      <LandingNav onGetStarted={() => navigate('/auth?mode=signup')} />

      {/* Hero Section - Full Screen with Warm Overlay */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden py-24"
      >
        <div 
          className="absolute inset-0 will-change-transform"
          style={{
            transform: prefersReducedMotion 
              ? 'none' 
              : `scale(${1 + (scrollY / (typeof window !== 'undefined' ? window.innerHeight : 800)) * 0.1})`,
          }}
        >
          <img
            src={heroImage}
            alt="Virtual staging"
            className="w-full h-full object-cover opacity-50"
            loading="eager"
          />
          {/* Warm gradient overlay - Atelier style */}
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/40 to-olive/30" />
        </div>

        {/* Terracotta glow accent */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120vw] h-[30vh] bg-gradient-to-t from-terracotta/10 to-transparent blur-3xl pointer-events-none" />
        
        <div className="relative z-10 w-full max-w-xl mx-auto px-6">
          <div className="mb-8 text-center">
            <div className="mb-6 inline-flex items-center px-4 py-2 rounded-full bg-alabaster/20 backdrop-blur-sm border border-alabaster/30">
              <span className="text-xs font-mono uppercase tracking-widest text-alabaster/90">
                {isLogin ? 'Welcome Back' : 'Get Started'}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-light tracking-tight mb-4 leading-none text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
              {isLogin ? 'Sign In' : 'Create Account'}
            </h1>
            <p className="text-lg md:text-xl text-alabaster/80 font-light leading-relaxed">
              {isLogin 
                ? 'Continue your staging journey' 
                : 'Start staging properties in seconds'}
            </p>
          </div>

          {/* Auth Card - Atelier Style */}
          <div className="bg-white/95 backdrop-blur-xl border border-stone shadow-soft-xl rounded-4xl p-8 md:p-10">
            {isLogin ? (
              <LoginForm onSwitchToSignup={() => setIsLogin(false)} />
            ) : (
              <SignupForm onSwitchToLogin={() => setIsLogin(true)} />
            )}
          </div>
        </div>
      </section>

      {/* Features Section - Alabaster Background */}
      <section className="relative py-32 px-6 bg-alabaster border-t border-stone">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              Why VirtualStagePro
            </div>
            <h2 className="text-5xl md:text-6xl font-serif font-light tracking-tight mb-6 text-charcoal">
              Professional staging
              <br />
              <span className="text-sand">at your fingertips</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-6xl md:text-7xl font-serif font-light text-olive/30 mb-6">
                01
              </div>
              <h3 className="text-2xl font-serif font-light mb-4 text-charcoal">Instant Results</h3>
              <p className="text-lg text-sand font-light leading-relaxed">
                AI-powered staging in seconds, not days
              </p>
            </div>
            <div className="text-center">
              <div className="text-6xl md:text-7xl font-serif font-light text-olive/30 mb-6">
                02
              </div>
              <h3 className="text-2xl font-serif font-light mb-4 text-charcoal">Save Thousands</h3>
              <p className="text-lg text-sand font-light leading-relaxed">
                Skip expensive physical staging costs
              </p>
            </div>
            <div className="text-center">
              <div className="text-6xl md:text-7xl font-serif font-light text-olive/30 mb-6">
                03
              </div>
              <h3 className="text-2xl font-serif font-light mb-4 text-charcoal">Multiple Styles</h3>
              <p className="text-lg text-sand font-light leading-relaxed">
                Modern, traditional, minimalist, and more
              </p>
            </div>
          </div>

          {/* Back to Home Link */}
          <div className="text-center mt-16">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sand hover:text-terracotta transition-colors duration-300 font-light"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
